import {
  getLoginSession,
  setLoginSession,
  type WorkspaceRole,
} from "@/lib/login-session"

export type ExamAppRole = "Admin" | "Faculty" | "Student"

export type ExamBlueprintRoleId = "program-director" | "faculty"

export interface ExamBlueprintRoleOption {
  id: ExamBlueprintRoleId
  label: string
  hint: string
  workspaceRole: WorkspaceRole
  signInFlowId: string
}

/**
 * The two roles the app asks about: the program director, and everyone else.
 * One choice, applied across the app: blueprints, category standards, and
 * what an author may tag. Aligned with Sign-in flow Super Admin and Faculty.
 */
export const EXAM_BLUEPRINT_ROLE_OPTIONS: readonly ExamBlueprintRoleOption[] = [
  {
    id: "program-director",
    label: "Program director",
    hint: "Manage blueprints and category standards. Tag with every standard.",
    workspaceRole: "administrator",
    signInFlowId: "role-super-admin",
  },
  {
    id: "faculty",
    label: "Other",
    hint: "Write and tag questions. View and use blueprints and standards.",
    workspaceRole: "member",
    signInFlowId: "role-faculty",
  },
]

/** Maps the signed-in workspace role to the exam app role. */
export function examRoleFromWorkspaceRole(role: WorkspaceRole): ExamAppRole {
  if (role === "student") return "Student"
  if (role === "member") return "Faculty"
  return "Admin"
}

/** Program directors create and manage blueprints; faculty consume them. */
export function canManageAssessmentBlueprint(): boolean {
  return getLoginSession().role === "administrator"
}

export function examRoleFromLoginSession(): ExamAppRole {
  return examRoleFromWorkspaceRole(getLoginSession().role)
}

export function examBlueprintRoleFromSession(): ExamBlueprintRoleId {
  return getLoginSession().role === "administrator" ? "program-director" : "faculty"
}

/**
 * Switch blueprint capacity from the profile menu without signing out.
 * Keeps the current session and updates exam role immediately.
 */
export function applyExamBlueprintRole(roleId: ExamBlueprintRoleId): void {
  const option = EXAM_BLUEPRINT_ROLE_OPTIONS.find((entry) => entry.id === roleId)
  if (!option) return
  const session = getLoginSession()
  setLoginSession({ ...session, role: option.workspaceRole })
}
